import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LIMITS, ROLES, WARD_SCOPED_ROLES, type AccessUser, type Role, type RoleGrant } from '@mahallu/shared';
import { ApiError, access, loadErrorKey, useAccessUsers, useMe, useWards } from '../api';
import { ConfirmButton, FormFooter, FormSection, Modal, SelectField, TextField } from '../components/Form';
import { Icon } from '../components/Icon';
import { Badge, PageHeader } from '../components/Parts';
import { dateTime } from '../format';

const errorText = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

/** Roles this person may hand out: only an admin makes an admin. */
function grantable(myRole: Role | null | undefined): Role[] {
  return ROLES.filter((r) => r !== 'MAHALLU_ADMIN' || myRole === 'MAHALLU_ADMIN');
}

function GrantLabel({ g }: { g: RoleGrant }) {
  const { t } = useTranslation();
  return (
    <>
      {t(`role.${g.role}`)}
      {g.ward ? ` · ${g.ward.code}` : null}
    </>
  );
}

/** Role and, for ward-scoped roles, the ward it covers. Used by both forms. */
function RoleFields(p: { role: Role | ''; setRole: (r: Role | '') => void; ward: string; setWard: (w: string) => void }) {
  const { t } = useTranslation();
  const me = useMe();
  const wards = useWards();
  const scoped = p.role !== '' && WARD_SCOPED_ROLES.includes(p.role);
  return (
    <>
      <SelectField<Role>
        label={t('users.role')}
        value={p.role}
        onChange={p.setRole}
        required
        empty={t('form.choose')}
        options={grantable(me.data?.role).map((r) => ({ value: r, label: t(`role.${r}`) }))}
      />
      {scoped ? (
        <SelectField
          label={t('users.ward')}
          value={p.ward}
          onChange={p.setWard}
          required
          empty={t('form.choose')}
          hint={t('users.wardHint')}
          options={(wards.data ?? []).map((w) => ({ value: w.code, label: `${w.code} · ${w.nameEn}` }))}
        />
      ) : null}
    </>
  );
}

function InviteForm({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [identifier, setIdentifier] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role | ''>('');
  const [ward, setWard] = useState('');
  const save = useMutation({
    mutationFn: () =>
      access.invite({ identifier: identifier.trim(), displayName: name.trim(), role: role as Role, wardCode: role !== '' && WARD_SCOPED_ROLES.includes(role) ? ward : null }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['access'] });
      onClose();
    },
  });
  return (
    <Modal
      title={t('users.invite')}
      onClose={onClose}
      footer={<FormFooter onCancel={onClose} saving={save.isPending} saveLabel={t('users.invite')} error={save.isError ? errorText(save.error) : undefined} />}
    >
      <form
        id="active-form"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <FormSection title={t('users.section')}>
          <TextField label={t('users.identifier')} value={identifier} onChange={setIdentifier} maxLength={254} required inputMode="text" autoComplete="off" hint={t('users.identifierHint')} />
          <TextField label={t('users.name')} value={name} onChange={setName} maxLength={LIMITS.name} required />
          <RoleFields role={role} setRole={setRole} ward={ward} setWard={setWard} />
        </FormSection>
      </form>
    </Modal>
  );
}

function ManageForm({ user, onClose }: { user: AccessUser; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [role, setRole] = useState<Role | ''>('');
  const [ward, setWard] = useState('');
  const [note, setNote] = useState('');
  // Every change answers with the updated person; put it straight into the list.
  const change = useMutation({
    mutationFn: (fn: () => Promise<AccessUser>) => fn(),
    onSuccess: (updated) => {
      qc.setQueryData<AccessUser[]>(['access'], (list) => list?.map((u) => (u.membershipId === updated.membershipId ? updated : u)));
      setNote(t('seq.saved'));
    },
    onMutate: () => setNote(''),
  });
  const id = user.membershipId;
  const active = user.status === 'ACTIVE';
  return (
    <Modal
      title={`${t('users.manage')} · ${user.displayName}`}
      onClose={onClose}
      footer={
        <>
          {change.isError ? (
            <p className="form-error" role="alert">
              {errorText(change.error)}
            </p>
          ) : note ? (
            <p className="note" role="status">
              {note}
            </p>
          ) : null}
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {t('users.done')}
          </button>
        </>
      }
    >
      <FormSection title={t('users.rolesSection')}>
        <ul className="grant-list full">
          {user.grants.map((g) => (
            <li key={g.id}>
              <Badge tone={g.role === user.primaryRole ? 'info' : 'neutral'}>
                <GrantLabel g={g} />
              </Badge>
              <span className="spacer" />
              <button
                type="button"
                className="icon-btn"
                title={t('users.removeRole')}
                aria-label={`${t('users.removeRole')}: ${t(`role.${g.role}`)}`}
                disabled={change.isPending || user.grants.length < 2}
                onClick={() => change.mutate(() => access.removeGrant(id, g.id))}
              >
                <Icon name="x" />
              </button>
            </li>
          ))}
        </ul>
      </FormSection>
      {active ? (
        <form
          className="add-role"
          onSubmit={(e) => {
            e.preventDefault();
            change.mutate(async () => {
              const out = await access.grant(id, { role: role as Role, wardCode: role !== '' && WARD_SCOPED_ROLES.includes(role) ? ward : null });
              setRole('');
              setWard('');
              return out;
            });
          }}
        >
          <FormSection title={t('users.addRole')}>
            <RoleFields role={role} setRole={setRole} ward={ward} setWard={setWard} />
            <div className="fld full">
              <button type="submit" className="btn sm primary" disabled={change.isPending || role === ''}>
                <Icon name="plus" />
                {t('users.addRole')}
              </button>
            </div>
          </FormSection>
        </form>
      ) : null}
      <FormSection title={t('users.mfaSection')}>
        <div className="fld full action-row">
          <span className="note">{t('users.resetMfaHint')}</span>
          <ConfirmButton label={t('users.resetMfa')} disabled={!user.mfaEnrolled || change.isPending} onConfirm={() => change.mutate(() => access.resetAuthenticator(id))} />
        </div>
      </FormSection>
      <FormSection title={t('users.accessSection')}>
        <div className="fld full action-row">
          <span className="note">{active ? t('users.revokeHint') : t('users.restoreHint')}</span>
          {active ? (
            <ConfirmButton danger label={t('users.revoke')} disabled={change.isPending} onConfirm={() => change.mutate(() => access.setStatus(id, 'REVOKED'))} />
          ) : (
            <button type="button" className="btn sm primary" disabled={change.isPending} onClick={() => change.mutate(() => access.setStatus(id, 'ACTIVE'))}>
              {t('users.restore')}
            </button>
          )}
        </div>
      </FormSection>
    </Modal>
  );
}

/** Settings › Users & roles (pilot slice 4): who can sign in, as what, and where. */
export function UsersRoles() {
  const { t, i18n } = useTranslation();
  const users = useAccessUsers();
  const [inviting, setInviting] = useState(false);
  const [managing, setManaging] = useState<string | null>(null);
  const managed = users.data?.find((u) => u.membershipId === managing);
  const canManage = users.isSuccess;

  return (
    <>
      <PageHeader
        icon="shield"
        title={t('users.title')}
        desc={t('users.desc')}
        actions={canManage ? [{ label: t('users.invite'), icon: 'plus', primary: true, onClick: () => setInviting(true) }] : []}
      />
      {users.isError ? <p className="form-error">{t(loadErrorKey(users.error))}</p> : null}
      <div className="tbl-wrap">
        <div className="tbl-scroll">
          <table>
            <thead>
              <tr>
                <th>{t('users.person')}</th>
                <th>{t('users.roles')}</th>
                <th>{t('users.twoStep')}</th>
                <th>{t('users.lastSeen')}</th>
                <th>{t('users.status')}</th>
                <th>{t('col.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {users.isLoading ? (
                <tr>
                  <td colSpan={6} className="tbl-msg">
                    {t('users.loading')}
                  </td>
                </tr>
              ) : null}
              {users.data?.map((u) => (
                <tr key={u.membershipId} className={u.status === 'REVOKED' ? 'muted-row' : undefined}>
                  <td>
                    <b>{u.displayName}</b>
                    {u.isYou ? (
                      <>
                        {' '}
                        <Badge tone="teal">{t('users.you')}</Badge>
                      </>
                    ) : null}
                    <div className="note">{u.destination}</div>
                  </td>
                  <td>
                    <span className="chip-row">
                      {u.grants.map((g) => (
                        <Badge key={g.id} tone={g.role === u.primaryRole ? 'info' : 'neutral'}>
                          <GrantLabel g={g} />
                        </Badge>
                      ))}
                    </span>
                  </td>
                  <td>
                    <Badge tone={u.mfaEnrolled ? 'ok' : 'warn'}>{u.mfaEnrolled ? t('users.twoStepOn') : t('users.twoStepOff')}</Badge>
                  </td>
                  <td className="num">{u.lastSeenAt ? dateTime(u.lastSeenAt, i18n.language) : t('users.never')}</td>
                  <td>
                    <Badge tone={u.status === 'ACTIVE' ? 'ok' : 'neutral'}>{t(`users.${u.status}`)}</Badge>
                  </td>
                  <td>
                    {u.isYou ? null : (
                      <button type="button" className="icon-btn" title={t('users.manage')} aria-label={`${t('users.manage')}: ${u.displayName}`} onClick={() => setManaging(u.membershipId)}>
                        <Icon name="edit" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {inviting ? <InviteForm onClose={() => setInviting(false)} /> : null}
      {managed ? <ManageForm user={managed} onClose={() => setManaging(null)} /> : null}
    </>
  );
}
